import { useEffect, useState } from 'react';
import { teachingWeeks } from '../../core/calendar';
import { standardLookup, type CourseStandard } from '../../core/course';
import { schedule } from '../../core/schedule';
import type { UnitTopic } from '../../core/topics';
import type { Calendar, IsoDate } from '../../core/types';
import { call, courseLabel, planPath, toApiError, type ApiError, type CalendarCopy, type CalendarSaveResponse, type GetToken, type PlanSummary } from '../api';
import { linkTo, planWeekHref, PLANS_HREF } from '../navigation';
import { usePlan } from '../usePlan';
import { usePlanList } from '../usePlanList';
import { CalendarForm, type CalendarChoice } from './CalendarForm';
import { ConfirmDelete } from './ConfirmDelete';
import { CurrentUnitPicker, shouldAskCurrentUnit, useCurrentUnitAsk } from './CurrentUnitPicker';
import { NotInCourse, NotYetPlaced } from './NotYetPlaced';
import { PlanCalendar, useSelectedUnit } from './PlanCalendar';
import { plural, UnitRow, type EditRequest } from './UnitRow';

/** A plan that could not be loaded: gone, or retryable. */
export function PlanLoadFailure({ error, onRetry }: { error: ApiError; onRetry(): void }) {
  return <section className="notice" role="alert">
    <h1>{error.status === 404 ? 'We could not find this plan.' : 'We could not load this plan.'}</h1>
    <p>{error.status === 404 ? 'It may have been deleted.' : error.message}</p>
    <div className="page-actions"><a className="secondary button-link" {...linkTo(PLANS_HREF)}>All plans</a>{error.status !== 404 && <button className="secondary" type="button" onClick={onRetry}>Try again</button>}</div>
  </section>;
}

/** The teacher's other plans as calendar choices (R3). */
export const calendarChoices = (plans: readonly PlanSummary[], planId: string): CalendarChoice[] =>
  plans.filter(p => p.id !== planId).map(p => ({ id: p.id, label: courseLabel(p.course), calendar: p.calendar }));

/** Each plan a save copied the calendar to, named, with how far it now runs past its last day. */
export const copyReport = (copies: readonly CalendarCopy[], plans: readonly PlanSummary[]) =>
  copies.flatMap(c => { const p = plans.find(x => x.id === c.planId); return p ? [{ ...c, label: courseLabel(p.course) }] : []; });

/** The plan list after a save copied `calendar` to `planIds`, so the next defaults compare against it. */
export const withCopies = (plans: readonly PlanSummary[], planIds: readonly string[], calendar: Calendar): PlanSummary[] =>
  plans.map(p => (planIds.includes(p.id) ? { ...p, calendar } : p));

/** Which other plans a calendar save also updated, and any that now run past their last day (R3). */
function CopyReport({ report, planbookToday }: { report: ReturnType<typeof copyReport>; planbookToday: IsoDate | null }) {
  return <div className={report.length ? 'notice inline-notice' : undefined} role="status">
    {report.length > 0 && <p>Also updated {report.map(r => r.label).join(', ')}.</p>}
    {report.filter(r => r.weeksOver > 0).map(r => <p key={r.planId}>
      {r.label} now runs {plural(r.weeksOver, 'week')} past its last day.{' '}
      <a {...linkTo(planWeekHref(r.planId, planbookToday))} aria-label={`Open ${r.label} this week`}>Open its week</a>
    </p>)}
  </div>;
}

/**
 * The plan editor (R6, R10, R11, R14, R17). Every change is one POST to the edits route, and
 * the editor redraws from the plan the Worker returns (`usePlan`). Unit dates come from the
 * shared `schedule` (KTD5). With the planbook on, its weeks link to the planbook's week.
 */
export function PlanPage({ planId, getToken, fresh = false, planbook = false, onDeleted, onOpenTopic }: {
  planId: string; getToken: GetToken; fresh?: boolean; planbook?: boolean; onDeleted(): void; onOpenTopic(topic: UnitTopic, unitId: string): void;
}) {
  const { data, loadError, retry, busy, error, send, edit: save } = usePlan(planId, getToken);
  const ask = useCurrentUnitAsk();
  const { selectedUnitId, select, container } = useSelectedUnit();
  const [editingCalendar, setEditingCalendar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // The teacher's plans for calendar copies; null while loading or when the list failed (no choices).
  const [plans, setPlans] = usePlanList(getToken);
  const [copies, setCopies] = useState<ReturnType<typeof copyReport>>([]);

  const edit = async (change: EditRequest) => {
    const before = data?.plan.plan.currentUnitId;
    const response = await save(change);
    // Clearing the current unit on a built plan asks again (R17).
    if (before && response && !response.plan.plan.currentUnitId) ask.askAgain();
    return response !== null;
  };
  const saveCalendar = async (calendar: Calendar, alsoPlanIds: string[] = []) => {
    const response = await send(`${planPath(planId)}/calendar`, 'PUT', alsoPlanIds.length ? { calendar, alsoPlanIds } : { calendar }) as CalendarSaveResponse | null;
    if (!response) return;
    const copied = response.copies ?? [];
    setCopies(plans ? copyReport(copied, plans) : []);
    if (plans) setPlans(withCopies(plans, copied.map(c => c.planId), response.plan.plan.calendar));
    setEditingCalendar(false);
  };

  async function deletePlan() {
    setDeleteBusy(true); setDeleteError(null);
    try { await call(getToken, planPath(planId), { method: 'DELETE' }); setDeleting(false); onDeleted(); }
    catch (failure) { setDeleteError(toApiError(failure).message); }
    finally { setDeleteBusy(false); }
  }

  const title = data ? courseLabel(data.plan.course) : null;
  useEffect(() => { if (title) document.title = `${title} · 240 Planner`; }, [title]);

  if (loadError) return <PlanLoadFailure error={loadError} onRetry={retry} />;
  if (!data) return <p className="notice" role="status">Loading your plan…</p>;

  const { plan: record, today, overrun } = data;
  const { plan } = record;
  const rows = schedule(plan, plan.calendar);
  const available = teachingWeeks(plan.calendar).length;
  const planned = plan.units.reduce((sum, u) => sum + u.weeks, 0);
  // Every standard grouped by where it sits, in plan order: one pass, not one per unit.
  const textOf = standardLookup(record.standards);
  const inUnit = new Map<string, CourseStandard[]>();
  const notYetPlaced: CourseStandard[] = [];
  const notInCourse: CourseStandard[] = [];
  for (const s of plan.standards) {
    const standard = textOf(s.id);
    if (s.placement.place === 'unit') {
      const list = inUnit.get(s.placement.unitId);
      if (list) list.push(standard); else inUnit.set(s.placement.unitId, [standard]);
    } else if (s.placement.place === 'not_yet_placed') notYetPlaced.push(standard);
    else notInCourse.push(standard);
  }
  // Coverage: standards in units, of every course standard the teacher has not set aside.
  const scheduled = plan.standards.length - notYetPlaced.length - notInCourse.length;
  const courseStandards = plan.standards.length - notInCourse.length;
  const asking = ask.asking(shouldAskCurrentUnit({ plan, fresh, builtAt: record.builtAt, today }));
  const current = rows.find(u => u.id === plan.currentUnitId);
  const planbookToday = planbook ? today : null;

  return <div className="plan-editor has-calendar">
    <div className="page-heading">
      <div>
        <a className="topic-back" {...linkTo(PLANS_HREF)}>← All plans</a>
        <h1 className="setup-title">{title}</h1>
        <p className={`plan-summary${planned > available ? ' over' : ''}`}><strong>{planned} of {available} weeks planned</strong>{courseStandards > 0 && <> · <span>{scheduled} of {courseStandards} standards scheduled</span></>}</p>
        {overrun.weeksOver > 0 && <p className="unit-flag" role="status">The plan runs {plural(overrun.weeksOver, 'week')} past the last day: {overrun.units.map(u => `${u.name} (${plural(u.weeksOver, 'week')})`).join(', ')}.</p>}
        <p className="muted">{current ? <>Your class is on <strong>{current.name}</strong>.</> : 'No current unit set.'}</p>
      </div>
      <div className="page-actions">
        <a className="primary button-link" {...linkTo(planWeekHref(planId, planbookToday))}>This week</a>
        {plan.units.length > 0 && <button className="secondary" type="button" onClick={ask.open}>{current ? 'Change current unit' : 'Set current unit'}</button>}
        <button className="secondary" type="button" aria-expanded={editingCalendar} onClick={() => { setCopies([]); setEditingCalendar(o => !o); }}>Change calendar</button>
        <button className="text-button danger" type="button" onClick={() => { setDeleteError(null); setDeleting(true); }}>Delete plan</button>
      </div>
    </div>
    <div className="plan-layout">
      <div className="plan-layout-calendar"><PlanCalendar rows={rows} calendar={plan.calendar} today={today} selectedUnitId={selectedUnitId} onSelectUnit={select} /></div>
      <div className="plan-layout-main" ref={container}>
        {record.standardsUnavailable && <div className="notice inline-notice"><p>We could not load the official standards for this course, so this plan has named units only.</p></div>}
        {editingCalendar && <section className="setup-card" aria-label="School calendar"><CalendarForm initial={plan.calendar} others={plans ? calendarChoices(plans, planId) : undefined} submitLabel="Save calendar" busy={busy} onSubmit={(c, also) => void saveCalendar(c, also)} onCancel={() => setEditingCalendar(false)} /></section>}
        <CopyReport report={copies} planbookToday={planbookToday} />
        {error && <p className="error-message" role="alert">{error}</p>}
        <p className="sr-only" role="status">{busy ? 'Saving…' : ''}</p>
        <div className="unit-list">
          {rows.map((unit, i) => <UnitRow key={unit.id} planId={planId} unit={unit} index={i} count={rows.length} current={unit.id === plan.currentUnitId} selected={unit.id === selectedUnitId}
            units={plan.units} standards={inUnit.get(unit.id) ?? []} getToken={getToken} onEdit={e => void edit(e)} onOpenTopic={onOpenTopic} />)}
          {rows.length === 0 && <p className="notice">No units yet. Add one, then move standards into it.</p>}
          <button className="secondary add-unit" type="button" onClick={() => void edit({ type: 'add_unit' })}>Add a unit</button>
        </div>
        <NotYetPlaced standards={notYetPlaced} units={plan.units} onEdit={e => void edit(e)} />
        <NotInCourse standards={notInCourse} onEdit={e => void edit(e)} />
      </div>
    </div>
    {asking && <CurrentUnitPicker units={rows} currentUnitId={plan.currentUnitId} today={today} busy={busy}
      onClose={ask.close}
      onPick={async unitId => { if (await edit({ type: 'anchor', unitId })) ask.close(); }} />}
    {deleting && <ConfirmDelete name={title!} busy={deleteBusy} error={deleteError} onConfirm={() => void deletePlan()} onCancel={() => setDeleting(false)} />}
  </div>;
}
